import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const InternationalShippingBramptonPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    destination: ''
  });
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Quote Request Received!",
      description: "Our Brampton team will contact you within 24 hours with the best shipping rates."
    });
    setFormData({ name: '', email: '', phone: '', destination: '' });
  };

  const benefits = [
    {
      icon: MapPin,
      title: "Local Brampton Service",
      description: "Convenient pickup and drop-off options throughout Brampton, serving the entire community with dedicated local support."
    },
    {
      icon: DollarSign,
      title: "Best Rates Guaranteed",
      description: "Compare DHL, FedEx, UPS, and more to secure the most competitive rates for your international shipments from Brampton."
    },
    {
      icon: Truck,
      title: "Trusted Carrier Network",
      description: "Partner with major international carriers to ensure your parcels reach India and 185+ countries safely and on time."
    },
    {
      icon: Globe,
      title: "Door-to-Door Delivery",
      description: "Complete end-to-end service from your Brampton address to any destination worldwide with real-time tracking included."
    },
    {
      icon: Shield,
      title: "Customs Clearance Support",
      description: "Expert assistance with all customs documentation and clearance procedures to ensure hassle-free international delivery."
    },
    {
      icon: Users,
      title: "Relocation Specialists",
      description: "Special handling for large relocation shipments from Brampton with tailored solutions for your moving needs."
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Contact Shippzy Brampton",
      description: "Reach out via phone, email, or visit one of our convenient partner drop-off locations in Brampton."
    },
    {
      number: 2,
      title: "Get Instant Quote",
      description: "We weigh and measure your shipment, then compare rates from DHL, FedEx, UPS to find you the best price."
    },
    {
      number: 3,
      title: "Choose Your Carrier",
      description: "Select the carrier that best fits your budget and timeline. We handle all the paperwork and customs forms."
    },
    {
      number: 4,
      title: "Track Your Shipment",
      description: "Receive tracking details via email and monitor your parcel's journey from Brampton to its destination in real time."
    },
    {
      number: 5,
      title: "Delivery Confirmation",
      description: "Your parcel arrives safely at the destination with signature confirmation and delivery updates."
    }
  ];

  const shippableItems = [
    'Documents and business materials',
    'Personal belongings and gifts',
    'Electronics and gadgets',
    'Clothing and textiles',
    'Books and educational materials',
    'Household goods for relocation',
    'Non-perishable food items',
    'Footwear and accessories'
  ];

  const faqs = [
    {
      question: "How much does international shipping from Brampton cost?",
      answer: "Shipping costs depend on weight, dimensions, destination, and carrier selection. At Shippzy, we compare rates from DHL, FedEx, UPS, and other trusted carriers to provide you with the most competitive pricing. For doorstep delivery, rates start at $9.99/kg (23 kg minimum). For India airport-to-airport bulk shipments (100 kg minimum), rates are $7.99/kg. Contact us for an instant quote tailored to your specific needs."
    },
    {
      question: "Where can I drop off my parcel in Brampton?",
      answer: "Shippzy partners with multiple drop-off locations throughout Brampton for your convenience. We also offer pickup services from your home or business. Contact our team to find the nearest drop-off point or schedule a pickup at a time that works for you."
    },
    {
      question: "Can I ship to India from Brampton?",
      answer: "Absolutely! Brampton has a large community with ties to India, and we specialize in shipping parcels from Brampton to India. We offer door-to-door delivery, customs clearance support, and competitive rates on all shipments. Whether you're sending gifts, documents, or relocation items, we've got you covered."
    },
    {
      question: "Which carriers does Shippzy use for international shipping from Brampton?",
      answer: "We work with major international carriers including DHL, FedEx, UPS, and Purolator. By comparing rates across all carriers, we ensure you get the best combination of price, speed, and reliability for your international shipments from Brampton."
    },
    {
      question: "How long does international shipping from Brampton take?",
      answer: "Delivery times vary by destination and service selected. Express shipping to India typically takes 3-7 business days, while economy options may take 10-15 days. Shipments to Europe, the USA, and other destinations have similar timelines. We provide estimated delivery dates when you book your shipment."
    },
    {
      question: "Do you help with customs clearance for international shipments?",
      answer: "Yes! Shippzy provides comprehensive customs documentation support and works with experienced customs agents to ensure smooth clearance. We help you prepare all necessary paperwork and guide you through the process. However, any customs duties or taxes imposed by the destination country are the responsibility of the sender or recipient."
    },
    {
      question: "Can I track my international shipment from Brampton?",
      answer: "Yes, all shipments include full tracking capabilities. Once your parcel is dispatched from Brampton, you'll receive a tracking number via email that allows you to monitor its progress in real time until delivery."
    },
    {
      question: "What items are prohibited for international shipping from Brampton?",
      answer: "Prohibited items include liquids, flammable materials, weapons, explosives, live animals, perishable foods, cash, and certain electronics with large lithium batteries. Restrictions vary by destination country. Contact us for a complete list of prohibited and restricted items before shipping."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>International Shipping Brampton | Affordable Courier Services – Shippzy</title>
        <meta name="description" content="Ship internationally from Brampton with Shippzy. Affordable courier services to India and 185+ countries. Compare DHL, FedEx, UPS rates. Door-to-door delivery, customs clearance support, and local Brampton pickup available." />
        <meta name="keywords" content="international shipping Brampton, shipping to India from Brampton, international courier Brampton, cheap international shipping Brampton, parcel delivery Brampton, courier services Brampton, Brampton shipping rates, ship from Brampton to India" />
        <link rel="canonical" href="https://www.shippzy.ca/international-shipping-brampton" />
        
        {/* Open Graph */}
        <meta property="og:title" content="International Shipping Brampton | Affordable Courier – Shippzy" />
        <meta property="og:description" content="Ship internationally from Brampton. Compare DHL, FedEx, UPS rates. Save up to 75% with door-to-door delivery and customs support." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/international-shipping-brampton" />
        
        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "International Shipping from Brampton",
            "provider": {
              "@type": "Organization",
              "name": "Shippzy Inc.",
              "telephone": "+1-437-616-0758",
              "email": "ship@shippzy.ca",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "140 Advance Blvd, Unit 7",
                "addressLocality": "Brampton",
                "addressRegion": "ON",
                "postalCode": "L6T 4Z8",
                "addressCountry": "CA"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Brampton",
              "containedIn": {
                "@type": "State",
                "name": "Ontario"
              }
            },
            "serviceType": "International Courier Service",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "price": "9.99",
                "priceCurrency": "CAD",
                "unitText": "per kg",
                "minWeight": "23 kg minimum"
              }
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                📦 Serving Brampton & GTA
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                International Shipping from Brampton
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy offers reliable and affordable international shipping from Brampton to India and over 185 countries worldwide. We partner with leading carriers including DHL, FedEx, and UPS to provide you with the best rates and fastest delivery times. Whether you're sending documents, gifts, or large relocation shipments, our Brampton-based team provides door-to-door service, professional customs clearance assistance, and convenient local pickup options.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Shippzy Brampton Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Brampton Customers Choose Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Local expertise combined with international reach. We understand Brampton's diverse shipping needs and deliver exceptional service every time.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How International Shipping Works from Brampton</h2>
              <p className="text-xl text-gray-600">Simple 5-step process to ship your parcels internationally from Brampton</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Can You Ship Section */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What Can You Ship Internationally from Brampton</h2>
              <p className="text-xl text-gray-600">
                We handle a wide range of shipment types for both personal and business needs
              </p>
            </div>

            <Card className="shadow-xl">
              <CardContent className="pt-8">
                <div className="grid md:grid-cols-2 gap-4">
                  {shippableItems.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-gray-800">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-4 bg-yellow-100 border-l-4 border-yellow-500 rounded">
                  <p className="text-sm text-gray-800">
                    <strong>Important:</strong> Some items are restricted or prohibited for international shipping. Contact our Brampton team for a complete list of restricted items specific to your destination country.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Popular Destinations Section */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Ship from Brampton to 185+ Countries</h2>
              <p className="text-xl text-gray-600">
                Popular destinations for Brampton international shipments
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { name: 'India', route: '/shipping-to-india-from-canada' },
                { name: 'Pakistan', route: '/shipping-canada-to-pakistan' },
                { name: 'United Kingdom', route: '/shipping-canada-to-uk' },
                { name: 'United States', route: null },
                { name: 'Jamaica', route: '/shipping-canada-to-caribbean' },
                { name: 'Philippines', route: '/shipping-canada-to-philippines' },
                { name: 'China', route: null },
                { name: 'Australia', route: '/shipping-canada-to-australia' }
              ].map((destination, index) => (
                <Card 
                  key={index} 
                  className={`text-center transition-all ${destination.route ? 'hover:shadow-lg hover:scale-105 cursor-pointer hover:border-red-500' : 'hover:shadow-lg'}`}
                  onClick={() => destination.route && navigate(destination.route)}
                >
                  <CardContent className="pt-6">
                    <Globe className="w-8 h-8 text-red-600 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900">{destination.name}</h3>
                    {destination.route && (
                      <p className="text-sm text-red-600 mt-2 font-semibold">Learn More →</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-600">
                Need to ship somewhere else? <a href="#quote-form" className="text-red-600 hover:text-red-700 font-semibold underline">Contact us</a> – we ship to over 185 countries worldwide!
              </p>
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Convenient Links</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Learn more about shipping to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options from Canada to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/relocation-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Relocation shipping services
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Toronto courier services
                </a>
                <span className="text-gray-400">•</span>
                <a href="/partner" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Find drop-off locations
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Everything you need to know about international shipping from Brampton</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA / Quote Form Section */}
        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship from Brampton?</h2>
              <p className="text-xl">Get your free international shipping quote today</p>
            </div>

            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">Our Brampton team will respond within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Destination Country</label>
                    <Input
                      name="destination"
                      value={formData.destination}
                      onChange={handleChange}
                      placeholder="e.g., India, Pakistan, UK"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Phone className="w-5 h-5" />
                  +1 (437) 616-0758
                </a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  <Mail className="w-5 h-5" />
                  ship@shippzy.ca
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Trusted International Courier Service in Brampton</h3>
            <p className="text-gray-300 mb-6">
              Ship internationally from Brampton with confidence. Compare rates from DHL, FedEx, UPS, and trusted carriers. Save up to 75% on international shipping with door-to-door delivery, customs clearance assistance, and local Brampton pickup.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => window.location.href = '/partner'}
              >
                Find Drop-Off Location
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                Explore All Services
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternationalShippingBramptonPage;